<div class="datasets-page">
    <div class="datasets-toolbar mat-elevation-z1">
        <form [formGroup]="filterForm">
            <mat-form-field class="w-100" subscriptSizing="dynamic">
                <mat-label>Business Domain</mat-label>
                <input
                    type="text"
                    matInput
                    formControlName="businessDomain"
                    [matAutocomplete]="businessDomainAuto"
                    placeholder="Search for a business domain"
                />
                <mat-autocomplete #businessDomainAuto="matAutocomplete" [displayWith]="getBusinessDomainName">
                    @for (businessDomain of filteredBusinessDomains$ | async; track businessDomain.id) {
                        <mat-option [value]="businessDomain">{{ businessDomain.name }}</mat-option>
                    }
                </mat-autocomplete>
            </mat-form-field>
        </form>
    </div>

    <div class="my-3">
        <mat-progress-spinner
            *ngIf="loadingResults()"
            mode="indeterminate"
            diameter="30"
            color="primary"
            style="margin: 20px auto; display: block"
        >
        </mat-progress-spinner>
    </div>

    @if (datasetsResponse$ | async; as response) {
        @if (dataCategories$ | async; as dataCategories) {
            <div class="datasets-body">
                <div class="filter-rail">
                    <button
                        mat-icon-button
                        type="button"
                        aria-label="Toggle filters"
                        matTooltip="Filters"
                        [color]="filterForm.controls.runMode.value ? 'primary' : ''"
                        (click)="filtersOpen.set(!filtersOpen())"
                    >
                        <mat-icon>filter_alt</mat-icon>
                    </button>
                </div>

                <mat-drawer-container class="datasets-drawer-container" [hasBackdrop]="false" [autosize]="true">
                    <mat-drawer mode="side" position="start" [opened]="filtersOpen()" class="filters-drawer">
                        <div class="filters-drawer-header">
                            <h3>Filters</h3>
                            <button mat-button type="button" (click)="onClearFilters()">Clear Filters</button>
                        </div>

                        <div class="filter-group">
                            <span class="filter-group-label">Dataset Readiness Status Filter</span>
                            <mat-checkbox
                                [checked]="filterForm.controls.runMode.value === 'DRAFT'"
                                (change)="onRunModeFilterChange('DRAFT', $event.checked)"
                            >
                                Draft
                            </mat-checkbox>
                            <mat-checkbox
                                [checked]="filterForm.controls.runMode.value === 'PUBLISHED'"
                                (change)="onRunModeFilterChange('PUBLISHED', $event.checked)"
                            >
                                Published
                            </mat-checkbox>
                        </div>
                    </mat-drawer>

                    <mat-drawer-content>
                        <form [formGroup]="assignForm" class="assign-toolbar">
                            <mat-form-field subscriptSizing="dynamic">
                                <mat-label>Select Data Category</mat-label>
                                <mat-select formControlName="dataCategoryId">
                                    @for (category of dataCategories; track category.id) {
                                        <mat-option [value]="category.id">{{ category.name || 'N/A' }}</mat-option>
                                    }
                                </mat-select>
                            </mat-form-field>

                            <button
                                mat-flat-button
                                color="primary"
                                type="button"
                                [disabled]="
                                    selection.isEmpty() ||
                                    assignForm.controls.dataCategoryId.value == null ||
                                    assigning()
                                "
                                (click)="onAssignDataCategory()"
                            >
                                Assign to {{ selection.selected.length }} selected
                            </button>

                            <button
                                mat-flat-button
                                color="warn"
                                type="button"
                                class="remove-button"
                                [disabled]="selection.isEmpty() || removingCategory()"
                                (click)="onRemoveDataCategory()"
                            >
                                Remove Category from {{ selection.selected.length }} selected
                            </button>

                            <button
                                mat-icon-button
                                type="button"
                                aria-label="Refresh datasets"
                                matTooltip="Refresh"
                                class="refresh-button"
                                [disabled]="loadingResults()"
                                (click)="onRefresh()"
                            >
                                <mat-icon>refresh</mat-icon>
                            </button>

                            <mat-form-field subscriptSizing="dynamic" class="search-field">
                                <mat-label>Search datasets</mat-label>
                                <input
                                    matInput
                                    type="text"
                                    [formControl]="filterForm.controls.searchValue"
                                    placeholder="Search by name"
                                />
                                @if (filterForm.controls.searchValue.value) {
                                    <button
                                        matSuffix
                                        mat-icon-button
                                        type="button"
                                        aria-label="Clear search"
                                        (click)="onClearSearch()"
                                    >
                                        <mat-icon>close</mat-icon>
                                    </button>
                                } @else {
                                    <mat-icon matSuffix>search</mat-icon>
                                }
                                @if (
                                    filterForm.controls.searchValue.value.length > 0 &&
                                    filterForm.controls.searchValue.value.length < minSearchLength
                                ) {
                                    <mat-hint>Type at least {{ minSearchLength }} characters to search</mat-hint>
                                }
                            </mat-form-field>
                        </form>

                        <div class="table-scroll-container mat-elevation-z2">
                            <table mat-table [dataSource]="response.datasets">
                                <ng-container matColumnDef="select">
                                    <th mat-header-cell *matHeaderCellDef>
                                        <mat-checkbox
                                            (change)="$event ? masterToggle(response.datasets) : null"
                                            [checked]="selection.hasValue() && isAllSelected(response.datasets)"
                                            [indeterminate]="selection.hasValue() && !isAllSelected(response.datasets)"
                                        >
                                        </mat-checkbox>
                                    </th>
                                    <td mat-cell *matCellDef="let dataset">
                                        <mat-checkbox
                                            (click)="$event.stopPropagation()"
                                            (change)="$event ? selection.toggle(getDatasetKey(dataset)) : null"
                                            [checked]="selection.isSelected(getDatasetKey(dataset))"
                                        >
                                        </mat-checkbox>
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="dataset">
                                    <th mat-header-cell *matHeaderCellDef>Dataset</th>
                                    <td mat-cell *matCellDef="let dataset">{{ dataset.dataset || 'N/A' }}</td>
                                </ng-container>

                                <ng-container matColumnDef="dataCategory">
                                    <th mat-header-cell *matHeaderCellDef>Data Category</th>
                                    <td mat-cell *matCellDef="let dataset">
                                        {{ getCategoryName(dataset.dataCategoryId, dataCategories) }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="dbName">
                                    <th mat-header-cell *matHeaderCellDef>Schema / Parent Folder</th>
                                    <td mat-cell *matCellDef="let dataset">{{ dataset.dbName || 'N/A' }}</td>
                                </ng-container>

                                <ng-container matColumnDef="tableName">
                                    <th mat-header-cell *matHeaderCellDef>Table / File Name</th>
                                    <td mat-cell *matCellDef="let dataset">{{ dataset.tableName || 'N/A' }}</td>
                                </ng-container>

                                <ng-container matColumnDef="runMode">
                                    <th mat-header-cell *matHeaderCellDef>Status</th>
                                    <td mat-cell *matCellDef="let dataset">{{ dataset.runMode || 'N/A' }}</td>
                                </ng-container>

                                <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                                <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
                            </table>
                        </div>

                        @if (response.datasets.length === 0) {
                            <p class="text-muted text-center my-3">No datasets found.</p>
                        }

                        <mat-paginator
                            [length]="response.totalCount"
                            [pageIndex]="response.pageNumber - 1"
                            [pageSize]="response.pageSize"
                            [pageSizeOptions]="pageSizeOptions"
                            (page)="onPageChange($event)"
                        >
                        </mat-paginator>
                    </mat-drawer-content>
                </mat-drawer-container>
            </div>
        }
    } @else {
        <p class="text-muted">Select a business domain to view its datasets.</p>
    }
</div>
